import { d as debounce } from './debounce-CBWnuiNx.js';

/*
Toggle a class on another element.

HTML Usage:
<toggle-element-class data-options='{}'>
  Toggle element class button
</toggle-element-class>
*/


class ToggleElementClass extends HTMLElement {
  connectedCallback() {
    // Options
    this.options = {
      toggleClass: 'toggle--is-active',
      target: 'body',
      toggleTriggerClass: '',
      preventDefault: true,
      stopPropagation: true,
      ariaExpanded: false,
    };

    // Get options from element data and combine with this.options
    if (this?.dataset?.options) {
      const dataOptions = JSON.parse(this.dataset.options);
      this.options = {
        ...this.options,
        ...dataOptions,
      };
    }

    // Reduce actions (So the event can also be removed)
    this.reducer = {
      click: (e) => this.debounceClickEvent(e),
      mouseEnter: (e) => this.debouncedOnMouse(e, 'add'),
      mouseLeave: (e) => this.debouncedOnMouse(e, 'remove'),
    };

    // Construct webcomponent
    this.construct();
  }

  /**
   * Construct webcomponent
   */
  construct() {
    this.target = document.querySelector(this.options.target);

    if (!this.target) return false;

    // Click
    this.removeEventListener('click', this.reducer.click);
    this.addEventListener('click', this.reducer.click);
  }

  /**
   * Debounce click event
   * @param {Object} e - The event object.
   * @returns {boolean} Whether the event was debounced.
   */
  debounceClickEvent(e) {
    if (!e) return false;

    this.preventDefault(e);
    this.stopPropagation(e);
    this.toggle();
  }

  /*
   * Debounced mouse event
   * @param {Object} e - The event object.
   * @param {String} type - The type of event.
   * @returns {boolean} Whether the event was debounced.
   */
  debouncedOnMouse(e, type) {
    if (!e) return false;

    const debouncer = debounce((type) => {
      switch (type) {
        case 'add':
          this.add();
          break;
        case 'remove':
          this.remove();
          break;
        default:
          this.toggle();
          break;
      }
    });
    debouncer(type);
  }

  /**
   * Prevent default
   * @param {Object} e - The event object.
   * @returns {boolean} Whether the event was prevented.
   */
  preventDefault(e) {
    if (this.options.preventDefault) e.preventDefault();
  }

  /**
   * Stop propagation
   * @param {Object} e - The event object.
   * @returns {boolean} Whether the event was stopped.
   */
  stopPropagation(e) {
    if (this.options.stopPropagation) e.stopPropagation();
  }

  /**
   * Add class
   * @returns {boolean} Whether the class was added.
   */
  add() {
    const { toggleClass, toggleTriggerClass } = this.options;
    if (this.target) this.target.classList.add(toggleClass);

    if (toggleTriggerClass) this.classList.add(toggleTriggerClass);

    // Switch aria-expanded
    if (this.options.ariaExpanded) this.setAttribute('aria-expanded', true);
  }

  /**
   * Remove class
   * @returns {boolean} Whether the class was removed.
   */
  remove() {
    const { toggleClass, toggleTriggerClass } = this.options;
    if (this.target) this.target.classList.remove(toggleClass);

    if (toggleTriggerClass) this.classList.remove(toggleTriggerClass);

    // Switch aria-expanded
    if (this.options.ariaExpanded) this.setAttribute('aria-expanded', false);
  }

  /**
   * Toggle class
   * @returns {boolean} Whether the class was toggled.
   */
  toggle() {
    const { toggleClass } = this.options;
    if (!this.target) return false;

    if (!this.target.classList.contains(toggleClass)) this.add();
    else this.remove();
  }
}

customElements.define('toggle-element-class', ToggleElementClass);
